import { EDRIntegration } from '@investigator/features/integrations/pages/IntegrationsConstants';
import {
  EntityLogscaleData,
  EntityDhcpData,
  EntityKnownData,
  OrganizedEDRData,
  EntityFieldLayout,
  EntityFieldLabel,
  EdrHost,
  EntityInformationField,
  EntityMetaData,
} from '../types';

/** EDR integration identifiers - direct reference to enum values */
export const EDR_SOURCE = {
  CROWDSTRIKE: EDRIntegration.CROWDSTRIKE,
  MICROSOFT_DEFENDER: EDRIntegration.MICROSOFT_DEFENDER,
  PALO_ALTO: EDRIntegration.PALO_ALTO_NETWORKS,
} as const;

/**
 * Field ordering configuration for different layouts.
 * - HalfWidth: 2-column layout (interleaved for CSS grid)
 * - FullWidth: 7-column layout (row by row)
 */
export const FIELD_ORDER_CONFIG: Record<EntityFieldLayout, EntityFieldLabel[]> =
  {
    [EntityFieldLayout.HalfWidth]: [
      // Column 1, Column 2 (interleaved for 2-column CSS grid)
      EntityFieldLabel.HostName,
      EntityFieldLabel.ASN,
      EntityFieldLabel.Locality,
      EntityFieldLabel.DHCP,
      EntityFieldLabel.MachineOS,
      EntityFieldLabel.ServiceName,
      EntityFieldLabel.Origin,
      EntityFieldLabel.ClientProtocols,
      EntityFieldLabel.Users,
      EntityFieldLabel.CertificateSubject,
      EntityFieldLabel.ServerSoftware,
      EntityFieldLabel.HostVLAN,
    ],
    [EntityFieldLayout.FullWidth]: [
      // Row 1: 7 fields
      EntityFieldLabel.HostName,
      EntityFieldLabel.MachineOS,
      EntityFieldLabel.Locality,
      EntityFieldLabel.Origin,
      EntityFieldLabel.ASN,
      EntityFieldLabel.DHCP,
      EntityFieldLabel.ServicePortProto,
      // Row 2: 6 fields
      EntityFieldLabel.ClientProtocols,
      EntityFieldLabel.Users,
      EntityFieldLabel.ServerSoftware,
      EntityFieldLabel.ServiceName,
      EntityFieldLabel.CertificateSubject,
      EntityFieldLabel.HostVLAN,
    ],
  };

/**
 * Sort fields according to the layout configuration
 */
const sortFieldsByLayout = (
  fields: EntityInformationField[],
  layout: EntityFieldLayout,
): EntityInformationField[] => {
  const order = FIELD_ORDER_CONFIG[layout];
  const fieldMap = new Map(fields.map((field) => [field.label, field]));

  return order
    .map((label) => fieldMap.get(label))
    .filter((field): field is EntityInformationField => field !== undefined);
};

interface LogscaleData {
  service?: string;
}

interface SelectedEntity {
  entityName?: string;
  entityCategory?: string;
  logscaleMetaData?: EntityLogscaleData;
  logscaleOrigData?: LogscaleData;
  logscaleRespData?: LogscaleData;
  logscaleDhcpData?: EntityDhcpData;
  logscaleKnownData?: EntityKnownData;
}

interface LoadingStates {
  edrLoading?: boolean;
  metaLoading?: boolean;
  origLoading?: boolean;
  respLoading?: boolean;
  dhcpLoading?: boolean;
  knownLoading?: boolean;
}

export interface FormatEntityFieldsParams {
  edrData: OrganizedEDRData | null;
  selectedEntity?: SelectedEntity | null;
  loadingStates: LoadingStates;
  layout?: EntityFieldLayout;
}

/**
 * Extract entity fields from EDR data and logscale data
 */
export const formatEntityFields = ({
  edrData,
  selectedEntity,
  loadingStates,
  layout = EntityFieldLayout.FullWidth,
}: FormatEntityFieldsParams): EntityInformationField[] => {
  // Extract from CrowdStrike
  const crowdstrike = edrData?.crowdstrike;
  // Extract from MS Defender
  const defender = edrData?.microsoft_defender;

  // Collect all host names from available sources with their EDR source
  const hostNamesWithSource: EdrHost[] = [];
  if (crowdstrike?.host_info?.host_name) {
    hostNamesWithSource.push({
      value: crowdstrike.host_info.host_name,
      source: EDR_SOURCE.CROWDSTRIKE,
    });
  }
  if (defender?.machine?.computerDnsName) {
    hostNamesWithSource.push({
      value: defender.machine.computerDnsName,
      source: EDR_SOURCE.MICROSOFT_DEFENDER,
    });
  }

  // Get OS from either source (convert null to undefined for type safety)
  const machineOs =
    crowdstrike?.host_info?.os ?? defender?.machine?.osPlatform ?? undefined;

  // Determine locality from logscale data based on entity category (source/destination)
  // local_orig/local_resp are strings: "true" = Internal, "false" = External
  const {
    entityCategory,
    logscaleMetaData,
    logscaleDhcpData,
    logscaleKnownData,
  } = selectedEntity || {};
  const locality = (() => {
    if (!logscaleMetaData) return undefined;

    // Use local_orig for source entities, local_resp for destination entities
    const localValue =
      entityCategory === 'source'
        ? logscaleMetaData.local_orig
        : entityCategory === 'destination'
          ? logscaleMetaData.local_resp
          : undefined;

    if (localValue === 'true') return 'Internal';
    if (localValue === 'false') return 'External';
    return undefined;
  })();

  // Logscale data - location info based on entity category
  // Use orig fields for source entities, resp fields for destination entities
  const isSourceEntity = entityCategory === 'source';
  const metaData: EntityMetaData = isSourceEntity
    ? {
        countryCode: selectedEntity?.logscaleMetaData?.['orig_cc'],
        country: selectedEntity?.logscaleMetaData?.['orig_geo.country'],
        city: selectedEntity?.logscaleMetaData?.['orig_geo.city'],
        asnValue: selectedEntity?.logscaleMetaData?.['id.orig_h.asn'],
        asnOrg: selectedEntity?.logscaleMetaData?.['id.orig_h.org'],
      }
    : {
        countryCode: selectedEntity?.logscaleMetaData?.['resp_cc'],
        country: selectedEntity?.logscaleMetaData?.['resp_geo.country'],
        city: selectedEntity?.logscaleMetaData?.['resp_geo.city'],
        asnValue: selectedEntity?.logscaleMetaData?.['id.resp_h.asn'],
        asnOrg: selectedEntity?.logscaleMetaData?.['id.resp_h.org'],
      };

  // Build origin display value
  const originParts = [metaData.city, metaData.country].filter(Boolean);
  const origin = originParts.length > 0 ? originParts.join(', ') : undefined;

  // ASN data
  const asn = [metaData.asnValue, metaData.asnOrg].filter(Boolean).join(', ');
  const clientProtocols = selectedEntity?.logscaleOrigData?.service;
  const serverProtocols = selectedEntity?.logscaleRespData?.service;

  // Extract DHCP data
  const dhcpClient = logscaleDhcpData?.client_addr;

  // Extract Known data - ensure arrays for multi-value fields
  // LogScale collect() may return single values as strings, so normalize to arrays
  const normalizeToArray = (
    value: string | string[] | undefined,
  ): string[] | undefined => {
    if (!value) return undefined;
    return Array.isArray(value) ? value : [value];
  };

  const userIds = normalizeToArray(logscaleKnownData?.['User IDs']);
  const certificateSubject = normalizeToArray(
    logscaleKnownData?.['Certificate Subject'],
  );
  const hostVlan = normalizeToArray(logscaleKnownData?.host_vlan);
  const software = normalizeToArray(logscaleKnownData?.Software);
  const availableServices = normalizeToArray(
    logscaleKnownData?.['Available Services'],
  );

  const {
    edrLoading,
    metaLoading,
    origLoading,
    respLoading,
    dhcpLoading,
    knownLoading,
  } = loadingStates;

  const fields: EntityInformationField[] = [
    {
      label: EntityFieldLabel.HostName,
      value: hostNamesWithSource[0]?.value,
      edrHosts:
        hostNamesWithSource.length > 0 ? hostNamesWithSource : undefined,
      isLoading: edrLoading,
    },
    {
      label: EntityFieldLabel.MachineOS,
      value: machineOs,
      hasOsIcon: true,
      isLoading: edrLoading,
    },
    { label: EntityFieldLabel.Locality, value: locality },
    {
      label: EntityFieldLabel.Origin,
      value: origin,
      locationData: metaData.countryCode
        ? {
            countryCode: metaData.countryCode,
            country: metaData.country,
            city: metaData.city,
          }
        : undefined,
      isLoading: metaLoading,
    },
    { label: EntityFieldLabel.ASN, value: asn, isLoading: metaLoading },
    { label: EntityFieldLabel.DHCP, value: dhcpClient, isLoading: dhcpLoading },
    {
      label: EntityFieldLabel.ServicePortProto,
      value: serverProtocols,
      isLoading: respLoading,
    },
    {
      label: EntityFieldLabel.ClientProtocols,
      value: clientProtocols,
      isLoading: origLoading,
    },
    {
      label: EntityFieldLabel.Users,
      value: userIds?.[0],
      values: userIds,
      isLoading: knownLoading,
    },
    {
      label: EntityFieldLabel.ServerSoftware,
      value: software?.[0],
      values: software,
      isLoading: knownLoading,
    },
    {
      label: EntityFieldLabel.ServiceName,
      value: availableServices?.[0],
      values: availableServices,
      isLoading: knownLoading,
    },
    {
      label: EntityFieldLabel.CertificateSubject,
      value: certificateSubject?.[0],
      values: certificateSubject,
      isLoading: knownLoading,
    },
    {
      label: EntityFieldLabel.HostVLAN,
      value: hostVlan?.[0],
      values: hostVlan,
      isLoading: knownLoading,
    },
  ];

  return sortFieldsByLayout(fields, layout);
};
